import { Button } from '@mui/material';
import { EnhancedAccordion } from 'components/enhanced/accordion/EnhancedAccordion';
import EnhancedDataGrid from 'components/enhanced/data-grid/EnhancedDataGrid';
import { required } from 'components/helpers/validators';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { examProductWarehouseService, examService, productService } from 'shared/services';
import { Option, OptionDescription } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { IExam } from '../types';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { productWarehouseService } from 'features/inventory/warehouses/api/product-warehouse.service';
import {
  EnhancedForm,
  IdentityField,
  AutocompleteAdapter,
  TextFieldAdapter
} from 'components/enhanced';
import { ExamProductColumns } from '../exam-products';

interface ExamProductFormParams {
  exam: IExam;
  disableEdit?: boolean;
}

function ExamProductForm({ exam, disableEdit }: ExamProductFormParams) {
  const translationPrefix = 'pages.exams.tabs.products';
  const { t } = useTranslation();

  const [products, setProducts] = useState<OptionDescription[]>([]);
  const [warehouses, setWarehouses] = useState<Option[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const datagridRefresh = useRef(null);

  useEffect(() => {
    warehouseService.getAllBaseInformation().then(setWarehouses);
  }, []);

  useEffect(() => {
    if (!selectedWarehouseId) {
      setProducts([]);
      return;
    }
    productService.getAllBaseInformation({ warehouse_id: selectedWarehouseId }).then((data) => {
      setProducts(data as Option[]);
    });
  }, [selectedWarehouseId]);

  const handleDelete = (id: number) => {
    examProductWarehouseService.delete(id).then((res) => {
      if (!res) {
        toast.error(t(`actions.failure`));
      } else {
        toast.success(t('actions.successfully.unlinked'));
      }
      datagridRefresh.current();
    });
  };

  const onAddProduct = async (values) => {
    productWarehouseService
      .getAllBaseInformation({
        warehouse_id: values.warehouse,
        product_id: values.product
      })
      .then((data) => {
        const productWarehouses = data;
        const linkedProductWarehouse = productWarehouses?.[0];

        if (!linkedProductWarehouse) {
          return toast.error(t(`${translationPrefix}.add_not_found`));
        }

        examService.assignProductWarehouse(exam.id, linkedProductWarehouse.id).then((res) => {
          if (!res) {
            toast.error(t(`actions.failure`));
          } else {
            toast.success(t('actions.successfully.associated'));
          }
          datagridRefresh.current();
        });
      });
  };

  const columns = ExamProductColumns(handleDelete);

  return (
    <div>
      {exam && (
        <>
          {!disableEdit && (
            <EnhancedAccordion
              title={t(`${translationPrefix}.title`)}
              subtitle={t(`${translationPrefix}.add_element`)}>
              <EnhancedForm
                onSubmit={onAddProduct}
                formId="addExamProduct"
                autoStyleLayout={true}
                columns={4}
                costumControls={
                  <Button type="submit" form="addExamProduct" variant="outlined" className="w-full">
                    {t('global.add')}
                  </Button>
                }>
                <IdentityField
                  name="warehouse"
                  component={AutocompleteAdapter}
                  options={warehouses}
                  label={t('global.warehouse')}
                  type="text"
                  validate={required}
                  setValue={(value) => setSelectedWarehouseId(value ? Number(value.id) : null)}
                  required
                />
                <IdentityField
                  gridSpan={2}
                  name="product"
                  component={AutocompleteAdapter}
                  options={products}
                  label={t('global.product')}
                  type="text"
                  disabled={!selectedWarehouseId}
                  optionLabel={'description'}
                  required
                />
                <IdentityField
                  name="quantity"
                  component={TextFieldAdapter}
                  label={t('global.quantity')}
                  type="number"
                />
              </EnhancedForm>
            </EnhancedAccordion>
          )}

          <EnhancedDataGrid
            service={examProductWarehouseService}
            columns={columns}
            outerParams={{
              exam: `${exam.id}`
            }}
            refresh={datagridRefresh}
          />
        </>
      )}
    </div>
  );
}

export default ExamProductForm;
